import { useRef, useState } from "react";
import type {
  ChallengeDetail,
  ChallengeHistoryItem,
} from "../../challenges/types/challengeAdmin";
import type {
  ChallengeEventDraft,
  ChallengeModalMode,
  DraftPair,
  DraftPlayer,
  DraftSideChoice,
  PairMode,
} from "../../challenges/types/challengeDraft";
import type {
  TournamentCategoryModalState,
  TournamentPairModalState,
} from "../../tournaments/types/tournamentAdmin";

export function useAdminChallengeEditorState() {
  const [selectedChallengeId, setSelectedChallengeId] = useState<number | null>(
    null,
  );
  const [challengeModalMode, setChallengeModalMode] =
    useState<ChallengeModalMode>("edit");
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [pairMode, setPairMode] = useState<PairMode>("fixed");
  const [pairCpf1, setPairCpf1] = useState("");
  const [pairCpf2, setPairCpf2] = useState("");
  const [tournamentPairModal, setTournamentPairModal] =
    useState<TournamentPairModalState | null>(null);
  const [tournamentPairModalSaving, setTournamentPairModalSaving] =
    useState(false);
  const [draftPairs, setDraftPairs] = useState<DraftPair[]>([]);
  const [drawCpf, setDrawCpf] = useState("");
  const [drawSideChoice, setDrawSideChoice] =
    useState<DraftSideChoice>("direita");
  const [draftPlayers, setDraftPlayers] = useState<DraftPlayer[]>([]);
  const [super8RankingPositionByCpf, setSuper8RankingPositionByCpf] = useState<
    Record<string, number>
  >({});
  const [challengeDetail, setChallengeDetail] =
    useState<ChallengeDetail | null>(null);
  const [activeTournamentCategoryKey, setActiveTournamentCategoryKey] =
    useState("");
  const [tournamentCategoryModal, setTournamentCategoryModal] =
    useState<TournamentCategoryModalState | null>(null);
  const [challengeScheduledAtDraft, setChallengeScheduledAtDraft] =
    useState<Date | null>(null);
  const [challengeScheduledEndAtDraft, setChallengeScheduledEndAtDraft] =
    useState<Date | null>(null);
  const [challengeDurationDraft, setChallengeDurationDraft] = useState("");
  const [challengeRoundsJson, setChallengeRoundsJson] = useState("[]");
  const [savingChallenge, setSavingChallenge] = useState(false);
  const [challengeEventDraft, setChallengeEventDraft] =
    useState<ChallengeEventDraft>({
      enabled: true,
      imageUrl: "",
      amountPerPlayer: "",
      savedAmountPerPlayerCents: null,
      registrationType: "individual",
      registrationsOpen: false,
      savedShirtSizesEnabled: false,
      savedShirtAvailabilityType: "none",
      savedShirtDeadlineAt: null,
      shirtSizesEnabled: false,
      shirtSizes: [],
      shirtAvailabilityType: "none",
      shirtDeadlineAt: "",
      shirtQuantityLimit: "",
      shirtConfirmedCount: 0,
      shirtAvailableCount: null,
      availabilityRestrictionsEnabled: false,
      availabilityRestrictionAmount: "",
      availabilityRestrictionLimit: "1",
      availabilityRestrictionDeadlineAt: "",
      availabilityRestrictionTypes: ["full_day", "day_period"],
      availabilityRestrictionPeriodsText: "",
    });
  const [confirmDeleteChallengeTarget, setConfirmDeleteChallengeTarget] =
    useState<ChallengeHistoryItem | null>(null);
  const [confirmDeleteChallengeLoading, setConfirmDeleteChallengeLoading] =
    useState(false);
  const [confirmDeleteChallengeError, setConfirmDeleteChallengeError] =
    useState("");
  const autoProvisionInFlightRef = useRef<Set<string>>(new Set());
  const tournamentDraftCategoryKeyRef = useRef("");
  const removedTournamentPairKeysRef = useRef<Set<string>>(new Set());

  return {
    activeTournamentCategoryKey,
    autoProvisionInFlightRef,
    challengeDetail,
    challengeDurationDraft,
    challengeEventDraft,
    challengeModalMode,
    challengeRoundsJson,
    challengeScheduledAtDraft,
    challengeScheduledEndAtDraft,
    confirmDeleteChallengeError,
    confirmDeleteChallengeLoading,
    confirmDeleteChallengeTarget,
    draftPairs,
    draftPlayers,
    drawCpf,
    drawSideChoice,
    pairCpf1,
    pairCpf2,
    pairMode,
    removedTournamentPairKeysRef,
    savingChallenge,
    selectedChallengeId,
    setActiveTournamentCategoryKey,
    setChallengeDetail,
    setChallengeDurationDraft,
    setChallengeEventDraft,
    setChallengeModalMode,
    setChallengeRoundsJson,
    setChallengeScheduledAtDraft,
    setChallengeScheduledEndAtDraft,
    setConfirmDeleteChallengeError,
    setConfirmDeleteChallengeLoading,
    setConfirmDeleteChallengeTarget,
    setDraftPairs,
    setDraftPlayers,
    setDrawCpf,
    setDrawSideChoice,
    setPairCpf1,
    setPairCpf2,
    setPairMode,
    setSavingChallenge,
    setSelectedChallengeId,
    setShowChallengeModal,
    setSuper8RankingPositionByCpf,
    setTournamentCategoryModal,
    setTournamentPairModal,
    setTournamentPairModalSaving,
    showChallengeModal,
    super8RankingPositionByCpf,
    tournamentCategoryModal,
    tournamentDraftCategoryKeyRef,
    tournamentPairModal,
    tournamentPairModalSaving,
  };
}
